import React, { useMemo } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { Box, Grid, GridItem, Text, Tooltip } from '@chakra-ui/react';
import PropTypes from 'prop-types';

import LoadingSpinner from '../../../../../../components/Spinner';
import { useInsuredAutomatedEmails } from '../../../../../../services/insured-service';
import { getInfiniteListData } from '../../../../../../services/util';
import { formatDateTimeToClientLocal } from '../../../../../../utils/dateUtil';
import { automatedEmailTypes } from '../util';

const NotificationHistoryBody = ({ insuredId, recordId, order }) => {
  const query = useInsuredAutomatedEmails({ insuredId, recordId, order });
  const { isLoading: emailsLoading } = query;

  const emailData = useMemo(() => getInfiniteListData(query.data), [query]);

  const dataLength = useMemo(() => {
    if (query.data?.pages?.length) {
      return query.data.pages[0].total;
    }

    return 0;
  }, [query]);

  if (emailsLoading) {
    return (
      <Box minH="100px">
        <LoadingSpinner minHeight="100px" />
      </Box>
    );
  }

  if (!emailData?.length) {
    return (
      <Box minH="100px">
        <Text
          color="greyScale.800"
          textAlign="center"
        >
          No Notification History
        </Text>
      </Box>
    );
  }

  return (
    <Box
      height="80vh"
      id="scrollableEmailHistoryBody"
      overflowY="auto"
    >
      <Grid
        alignItems="center"
        color="greyScale.800"
        gap={6}
        mb={2}
        px={2}
        templateColumns="repeat(10, 1fr)"
        textAlign="left"
        textStyle="bodySm500"
      >
        <GridItem colSpan={2}>Date</GridItem>
        <GridItem colSpan={3}>To</GridItem>
        <GridItem colSpan={5}>Email Type</GridItem>
      </Grid>
      {emailData.length && (
        <InfiniteScroll
          dataLength={dataLength}
          endMessage={(
            <Text color="greyScale.800" textAlign="center" textStyle="bodyMd">
              {`Showing all (${dataLength})`}
            </Text>
          )}
          hasMore={query.hasNextPage}
          loader={(
            <Text color="greyScale.800" textAlign="center" textStyle="bodyMd">
              Loading...
            </Text>
          )}
          next={() => query.fetchNextPage()}
          scrollableTarget="scrollableEmailHistoryBody"
          scrollThreshold={0.7}
        >
          {emailData.map(email => {
            const convertedDate = formatDateTimeToClientLocal(email.createdAt, 'MM/DD/YYYY – h:mm a');
            const emailTypeName = email.automatedEmailType?.name;
            const emailTypeInfo = automatedEmailTypes[emailTypeName] || { text: emailTypeName || 'Unknown Notification', tooltip: '' };
            const emailTypeText = emailTypeInfo.text;
            const emailTypeTooltip = emailTypeInfo.tooltip || emailTypeInfo.text;

            return (
              <Box
                bg="white"
                borderRadius="md"
                boxShadow="base2"
                key={email.id}
                mb={4}
                px={2}
                py={4}
              >
                <Grid
                  alignItems="center"
                  gap={6}
                  templateColumns="repeat(10, 1fr)"
                  textAlign="left"
                  textStyle="bodyMd"
                >
                  <GridItem colSpan={2}>{convertedDate}</GridItem>
                  <GridItem colSpan={3}>{email.to}</GridItem>
                  <GridItem colSpan={5}>
                    <Tooltip label={emailTypeTooltip}>
                      {emailTypeText}
                    </Tooltip>
                  </GridItem>
                </Grid>
              </Box>
            );
          })}
        </InfiniteScroll>
      )}
    </Box>
  );
};

NotificationHistoryBody.propTypes = {
  insuredId: PropTypes.string,
  recordId: PropTypes.string,
  order: PropTypes.string
};

export default NotificationHistoryBody;
